import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import useListenToRenderedQuestions from 'chameleon/packages/blue-book-player/hooks/useListenToRenderedQuestions';
import useGetPlayerEl from 'chameleon/packages/blue-book-player/hooks/useGetPlayerEl';
import useResetExpandedColumns from 'chameleon/packages/blue-book-player/hooks/useResetExpandedColumns';
import {
  addPropsToNodes,
  createContainer,
  getItemByRef,
  renderExpander,
  createColumnsGutter,
  getValidContentContainers,
} from 'chameleon/packages/blue-book-player/utils';
import { SELECTORS } from 'chameleon/packages/blue-book-player/constants/selectors';
import { KEY_CODES } from 'chameleon/packages/blue-book-player/constants/keyCodes';
import {
  TWO_COLUMNS_CLASS,
  ONE_COLUMN_CLASS,
} from 'chameleon/packages/blue-book-player/constants/columns';
import styles from 'chameleon/packages/blue-book-player/InnerContainer/TwoColumnsExpander.css';
import {
  runSetItemIsLoaded,
  addTwoColumnsWrapperClass,
  addClassesForHeightDivs,
  addPaddingToColumns,
  addWidthClassToColumns,
  addOverflowInRow,
  addWidthClassToColumn,
} from 'chameleon/packages/blue-book-player/InnerContainer/utils';
import { BluebookContext } from 'chameleon/packages/blue-book-player/state/BluebookContext';

const TwoColumnInjector = ({
  crossOutReducer,
  learnosityApp,
  setIsQuestionDisplayed,
}) => {
  const { state: playerState } = useContext(BluebookContext);
  const playerEl = useGetPlayerEl();
  const [loadedQuestions, setLoadedQuestions] = useState({});
  const [nodes, setNodes] = useState([]);
  const [expandedColumn, setExpandedColumn] = useState(null);
  const ExpandedColumnContext = createContext();
  const { isApcDataLoading, isLearnosityApiReady } = playerState;
  const isFirstItemLoading = useRef(true);

  useEffect(() => {
    const onKeyUp = (e) => {
      const { keyCode, ctrlKey, shiftKey } = e;
      const isExpandShortcut =
        ctrlKey && shiftKey && [KEY_CODES.r, KEY_CODES.l].includes(keyCode);
      if (isExpandShortcut) {
        e.preventDefault();
        e.stopPropagation();

        let column;
        if (keyCode === KEY_CODES.l) {
          column = 'left';
        } else if (keyCode === KEY_CODES.r) {
          column = 'right';
        }

        if (column) {
          setExpandedColumn(column);
        }
      }
    };
    document.addEventListener('keyup', onKeyUp);

    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, []);

  const doInject = (reference) => {
    if (!loadedQuestions[reference]) {
      setLoadedQuestions((currentLoadedQuestions) => {
        return {
          ...currentLoadedQuestions,
          [reference]: true,
        };
      });
      const questionEl = getItemByRef(playerEl, reference);
      if (questionEl.querySelector('[class*="-column-expander"]')) {
        return;
      }

      let twoColumnsFound = false;

      const itemQuestionsLength =
        learnosityApp?.getItems?.()?.[reference]?.questions?.length || 0;

      SELECTORS.TWO_COLUMNS.some((selectors) => {
        let {
          isContainerValid,
          passageContainerElement,
          questionContainerElement,
        } = getValidContentContainers(questionEl, selectors);
        if (isContainerValid && itemQuestionsLength === 1) {
          twoColumnsFound = true;
          questionEl.classList.add(TWO_COLUMNS_CLASS);

          selectors.forEach((selector, index) => {
            const isLeftColumn = index === 0;
            const className = isLeftColumn
              ? styles['left-column-expander']
              : styles['right-column-expander'];

            createContainer(
              selector,
              reference,
              playerEl,
              className,
              'prepend',
              'span',
            ).then((elements) => {
              let newNodes = [];
              (elements || []).forEach((optionElement) => {
                const node = renderExpander(optionElement.triggerContainer, {
                  column: isLeftColumn ? 'left' : 'right',
                  firstColumnEl: passageContainerElement,
                  secondColumnEl: questionContainerElement,
                });
                newNodes.push(node);
              });
              setNodes((currentNodes) => [...currentNodes, ...newNodes]);
            });
          });

          //Adding the classes after, to not change the elements reference
          passageContainerElement?.classList?.add('two-columns', 'left-column');
          questionContainerElement?.classList?.add(
            'two-columns',
            'right-column',
          );
          addTwoColumnsWrapperClass(passageContainerElement);
          addPaddingToColumns(
            passageContainerElement,
            questionContainerElement,
          );
          addWidthClassToColumns(
            passageContainerElement,
            questionContainerElement,
          );

          createColumnsGutter(passageContainerElement);
          runSetItemIsLoaded(questionEl, isFirstItemLoading, () => {
            setIsQuestionDisplayed(true);
          });

          return true;
        }
      });

      if (!twoColumnsFound) {
        questionEl?.classList?.add(ONE_COLUMN_CLASS);
        addClassesForHeightDivs(questionEl);
        addOverflowInRow(questionEl);
        if (itemQuestionsLength > 1) {
          questionEl?.classList?.add('with-multiple-answers');
        }

        runSetItemIsLoaded(questionEl, isFirstItemLoading, () => {
          setIsQuestionDisplayed(true);
        });
      }
      addWidthClassToColumn(questionEl);
    }
  };

  useResetExpandedColumns(
    {
      isLearnosityApiReady,
      learnosityApp,
      playerEl,
    },
    () => {
      setExpandedColumn(null);
    },
  );
  useListenToRenderedQuestions(playerState, learnosityApp, doInject);

  if (isApcDataLoading) {
    return null;
  }

  return (
    <ExpandedColumnContext.Provider value={expandedColumn}>
      {addPropsToNodes(nodes, {
        crossOutReducer,
        setExpandedColumn,
        expandedContext: ExpandedColumnContext,
      })}
    </ExpandedColumnContext.Provider>
  );
};

TwoColumnInjector.propTypes = {};

export default TwoColumnInjector;
